import { Sigma, BookOpen, Landmark, Globe2, Leaf, FlaskConical, Languages, Shapes, BookMarked, Palette, Music, Dumbbell, Cpu, Brain, Scale, Calculator, Atom, PenTool } from "lucide-react";

export const COLORS = {
  blue: { tone: "text-blue-600 bg-blue-500/10 dark:text-blue-300", dot: "bg-blue-500", label: "Bleu" },
  rose: { tone: "text-rose-600 bg-rose-500/10 dark:text-rose-300", dot: "bg-rose-500", label: "Rose" },
  amber: { tone: "text-amber-700 bg-amber-500/10 dark:text-amber-300", dot: "bg-amber-500", label: "Ambre" },
  teal: { tone: "text-teal-700 bg-teal-500/10 dark:text-teal-300", dot: "bg-teal-500", label: "Sarcelle" },
  emerald: { tone: "text-emerald-700 bg-emerald-500/10 dark:text-emerald-300", dot: "bg-emerald-500", label: "Émeraude" },
  violet: { tone: "text-violet-700 bg-violet-500/10 dark:text-violet-300", dot: "bg-violet-500", label: "Violet" },
  sky: { tone: "text-sky-700 bg-sky-500/10 dark:text-sky-300", dot: "bg-sky-500", label: "Ciel" },
  slate: { tone: "text-slate-700 bg-slate-500/10 dark:text-slate-300", dot: "bg-slate-500", label: "Ardoise" },
  orange: { tone: "text-orange-700 bg-orange-500/10 dark:text-orange-300", dot: "bg-orange-500", label: "Orange" },
  fuchsia: { tone: "text-fuchsia-700 bg-fuchsia-500/10 dark:text-fuchsia-300", dot: "bg-fuchsia-500", label: "Fuchsia" },
  lime: { tone: "text-lime-700 bg-lime-500/10 dark:text-lime-300", dot: "bg-lime-500", label: "Citron vert" },
  red: { tone: "text-red-600 bg-red-500/10 dark:text-red-300", dot: "bg-red-500", label: "Rouge" },
};

export const ICONS = {
  sigma: Sigma, book: BookOpen, landmark: Landmark, globe: Globe2, leaf: Leaf, flask: FlaskConical, languages: Languages,
  shapes: Shapes, bookmark: BookMarked, palette: Palette, music: Music, dumbbell: Dumbbell, cpu: Cpu, brain: Brain,
  scale: Scale, calculator: Calculator, atom: Atom, pen: PenTool,
};

const DEFAULTS = {
  "Mathématiques": ["blue", "sigma"], "Français": ["rose", "book"], "Histoire": ["amber", "landmark"],
  "Géographie": ["teal", "globe"], "SVT": ["emerald", "leaf"], "Physique-Chimie": ["violet", "flask"],
  "Anglais": ["sky", "languages"], "Autre": ["slate", "shapes"],
};
export const SUBJECTS = Object.keys(DEFAULTS).map((name) => ({ name }));
const COLOR_KEYS = Object.keys(COLORS);

export const subjectStyle = (name = "", styles = {}) => {
  const hash = [...name].reduce((h, ch) => (h * 31 + ch.charCodeAt(0)) >>> 0, 7);
  const [color, icon] = DEFAULTS[name] || [COLOR_KEYS[hash % COLOR_KEYS.length], "bookmark"];
  return { color: styles?.[name]?.color || color, icon: styles?.[name]?.icon || icon };
};
export const subjectMeta = (name = "", styles = {}) => {
  const s = subjectStyle(name, styles);
  return { name, icon: ICONS[s.icon] || BookMarked, tone: (COLORS[s.color] || COLORS.slate).tone };
};
export const subjectsOf = (user) => (user?.subjects?.length ? user.subjects : SUBJECTS.map((s) => s.name));

export const GRADIENTS = [
  { id: "electrique", name: "Électrique", from: "221 100% 58%", to: "258 90% 66%" },
  { id: "aurore", name: "Aurore", from: "330 85% 60%", to: "24 95% 58%" },
  { id: "ocean", name: "Océan", from: "190 90% 42%", to: "226 90% 58%" },
  { id: "menthe", name: "Menthe", from: "160 75% 38%", to: "190 85% 45%" },
  { id: "crepuscule", name: "Crépuscule", from: "262 80% 60%", to: "320 80% 60%" },
  { id: "solaire", name: "Solaire", from: "36 95% 50%", to: "350 85% 60%" },
];

export const PLANS = [
  { id: "free", name: "Gratuit", price: "0 €", period: "pour toujours", minutes: 60,
    features: ["60 minutes de transcription / mois", "Notes automatiques structurées", "Résumés simples", "Questions sur ton cours", "Export Markdown"] },
  { id: "premium", name: "Premium", price: "4,99 €", amount: 4.99, period: "/ mois", minutes: 600,
    features: ["600 minutes de transcription / mois", "Tout l'offre Gratuit", "Fiches de révision", "Quiz intelligents et flashcards", "Exportation PDF"] },
  { id: "pro", name: "Pro", price: "9,99 €", amount: 9.99, period: "/ mois", minutes: null, highlight: true,
    features: ["Transcription illimitée", "Toutes les fonctionnalités", "Recherche intelligente par IA", "Outils avancés", "Priorité de traitement"] },
];

export const usageText = (user) => {
  const u = user?.usage || { used_minutes: 0, limit_minutes: 60 };
  return u.limit_minutes == null ? `${Math.round(u.used_minutes)} min · illimité` : `${Math.round(u.used_minutes)} / ${u.limit_minutes} min`;
};

export const SOURCE_LABEL = { micro: "Enregistrement", import: "Import audio", texte: "Mode texte" };

export const fmtDuration = (sec = 0) => {
  const s = Math.round(sec);
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), r = s % 60;
  if (h) return `${h} h ${String(m).padStart(2, "0")}`;
  if (m) return `${m} min${r ? ` ${String(r).padStart(2, "0")} s` : ""}`;
  return `${r} s`;
};

export const fmtClock = (sec = 0) => {
  const h = Math.floor(sec / 3600), m = Math.floor((sec % 3600) / 60), s = sec % 60;
  return [h, m, s].map((v) => String(v).padStart(2, "0")).join(":");
};

export const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" }) : "";
